import { useMemo, useState, JSX, useEffect } from 'react'
import styles from './HomeScreen.module.css'

import illustration from '../../assets/images/illustration.png'
import widget1 from '../../assets/images/widget1.jpg'
import widget2 from '../../assets/images/widget2.jpg'
import playlist_cover from '../../assets/images/playlist_cover.jpg'
import playlist_image1 from '../../assets/images/playlist_image1.jpg'
import playlist_image2 from '../../assets/images/playlist_image2.jpg'
import playlist_image3 from '../../assets/images/playlist_image3.jpg'
import playlist_image4 from '../../assets/images/playlist_image4.jpg'

import WidgetImage from '../../components/Widgets/WidgetImage'
import WidgetClock from '../../components/Widgets/WidgetClock'
import WidgetCalendar from '../../components/Widgets/WidgetCalendar'
import Dock from '../../components/Dock/Dock'
import AboutMeWindow from '../../components/AboutMe/AboutMeWindow'
import FaqWindow from '../../components/Faq/FaqWindow'
import FinderWindow from '../../components/Finder/FinderWindow'
import GalleryWindow from '../../components/Gallery/GalleryWindow'
import WidgetPlaylist from '../../components/Widgets/WidgetPlaylist'
import StatusBar from '../../components/StatusBar/StatusBar'
import NotificationContainer from '../../components/Notification/NotificationContainer'
import { translations } from '../../translations'
import { useTranslationUpdate } from '../../hooks/useTranslationUpdate'
import { useNotification } from '../../hooks/useNotification'
import EmailWindow from '../../components/Email/EmailWindow'
import TrashWindow from '../../components/Trash/TrashWindow'

export type App = {
  name: string
  icon: string
  component?: (props: { onClose: () => void; isMinimized: boolean; onToggleMinimize: () => void }) => JSX.Element | null
}

const pinnedDock: App[] = [
  { name: 'About', icon: '/images/apps/about.png', component: AboutMeWindow },
  { name: 'Project', icon: '/images/apps/finder.png', component: FinderWindow },
  { name: 'Images', icon: '/images/apps/gallery.png', component: GalleryWindow },
  { name: 'Faq', icon: '/images/apps/faq.png', component: FaqWindow },
  { name: 'Message', icon: '/images/apps/email.png', component: EmailWindow },
]

const specialApps: App[] = [
  { name: 'Trash', icon: '/images/apps/trash.svg', component: TrashWindow }
]

type HomeScreenProps = {
  isLocked?: boolean
}

export default function HomeScreen({ isLocked = false }: HomeScreenProps) {
  const [openApp, setOpenApp] = useState<App | null>(null)
  const [minimizedApps, setMinimizedApps] = useState<string[]>([])
  const [runningApps, setRunningApps] = useState<App[]>([])
  
  const { notifications, addNotificationOnce, removeNotification } = useNotification()
  
  useTranslationUpdate()

  const allLaunchable = useMemo(() => {
    const allApps = [...pinnedDock, ...specialApps].filter(a => !!a.component)
    return new Map(allApps.map(a => [a.name, a]))
  }, [])

  useEffect(() => {
    if (isLocked) return 
    
    const welcomeTimer = setTimeout(() => {
      addNotificationOnce('welcome', {
        title: translations('notifications.title'),
        message: translations('notifications.welcome'),
        icon: '/images/apps/email.png'
      })
    }, 2000)

    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window
    const mobileTimer = isMobile ? setTimeout(() => {
      addNotificationOnce('mobile-warning', {
        title: translations('mobile_warning.title'),
        message: translations('mobile_warning.message'),
        icon: '/images/icons/computer.svg'
      })
    }, 8000) : null

    return () => {
      clearTimeout(welcomeTimer)
      if (mobileTimer) clearTimeout(mobileTimer)
    }
  }, [addNotificationOnce, isLocked])

  const toggleMinimize = () => {
    if (!openApp) return
    setMinimizedApps(p => (p.includes(openApp.name) ? p.filter(n => n !== openApp.name) : [...p, openApp.name]))
  }

  const closeActiveApp = () => {
    if (!openApp) return
    const closing = openApp
    setOpenApp(null)
    setRunningApps(prev => prev.filter(a => a.name !== closing.name))
    setMinimizedApps(prev => prev.filter(n => n !== closing.name))
  }

  const onDockClick = (name: string) => {
    const running = runningApps.find(a => a.name === name)
    if (running) {
      if (openApp?.name === name) {
        toggleMinimize()
        return
      }
      setOpenApp(running)
      setMinimizedApps(prev => prev.filter(n => n !== name))
      return
    }
    const launchable = allLaunchable.get(name)
    if (launchable) {
      setOpenApp(launchable)
      setRunningApps(prev => (prev.some(a => a.name === launchable.name) ? prev : [...prev, launchable]))
      setMinimizedApps(prev => prev.filter(n => n !== launchable.name))
    }
  }

  return (
    <div className={styles.container}>
      <StatusBar />
      
      <NotificationContainer 
        notifications={notifications}
        onClose={removeNotification}
      />
      
      <section className={styles.grid}>
        <div className={`${styles.card} ${styles.large}`}>
          <WidgetPlaylist coverSrc={playlist_cover} title="code ღ" subtitle="spotify" primaryColor="#54703F" secondColor="#BDCDB2" thumbs={[playlist_image1, playlist_image2, playlist_image3, playlist_image4]} />
        </div>
        <div className={`${styles.card} ${styles.s1}`}>
          <WidgetClock />
        </div>
        <div className={`${styles.card} ${styles.s2}`}>
          <WidgetCalendar />
        </div>
        <div className={`${styles.card} ${styles.s3}`}>
          <WidgetImage size="small" src={widget1} alt="Imagem pequena 1" />
        </div>
        <div className={`${styles.card} ${styles.s4}`}>
          <WidgetImage size="small" src={widget2} alt="Imagem pequena 2" />
        </div>
      </section>

      <Dock pinned={pinnedDock} running={runningApps} activeAppName={openApp?.name} minimizedApps={minimizedApps} onClickApp={onDockClick} />

      {openApp?.component && (
        <openApp.component onClose={closeActiveApp} isMinimized={minimizedApps.includes(openApp.name)} onToggleMinimize={toggleMinimize} />
      )}

      <img src={illustration} alt="Garota com gato" className={styles.girlImage} />
      <div className={styles.bottomBar} />
    </div>
  )
}